/* eslint-disable @typescript-eslint/no-unused-vars */
import { makeStateStore } from '@playcastdotio/StateStore';
import { PlaycastPeerServiceType } from './types';
import { H264EncodingConfig, PlaycastChannels, PlaycastWebrtcPreset, PlaycastWebrtcPresetGroup } from './typesMedia';

const KBit = 1000;
const MBit = KBit * KBit;
const GBit = MBit * MBit;

// Shuffle an array
function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

// Generate a range of numbers
function generateRange(min: number, max: number): number[] {
  const range: number[] = [];
  for (let i = min; i <= max; i++) {
    range.push(i);
  }
  return range;
}

interface IceServer {
  urls: string;
  username?: string;
  credential?: string;
}

function generateIceServers(useTurn: boolean): IceServer[] {
  // Free STUN servers exist; see https://dev.to/alakkadshaw/google-stun-server-list-21n4 https://gist.github.com/mondain/b0ec1cf5f60ae726202e
  // { urls: 'stun:stun.l.google.com:19302' },

  const stunServers: IceServer[] = shuffleArray(generateRange(1, 4)).map((num) => ({
    urls: `stun:stun${num}.playcast.io:5349`,
    username: 'stunadmin',
    credential: 'm7te8VLHjDi4sA6KTG8g',
  }));

  if (useTurn) {
    const turnServers: IceServer[] = shuffleArray(generateRange(1, 24)).map((num) => ({
      urls: `turn:turn${num}.playcast.io:5349`,
      username: 'stunadmin',
      credential: 'm7te8VLHjDi4sA6KTG8g',
    }));

    return [...stunServers, ...turnServers];
  } else {
    return stunServers;
  }
}

export const serviceConfigs: PlaycastPeerServiceType = {
  peerOptions: {
    enableDataChannels: true,
    batchCandidates: true,
    batchCandidatesTimeout: 100,
    offerOptions: {
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
      iceRestart: true,
    },
    config: {
      bundlePolicy: 'max-bundle',
      iceTransportPolicy: 'all',
      iceCandidatePoolSize: 2,
      rtcpMuxPolicy: 'require',
      // ICE servers will be provided by the caller
      //iceTransportPolicy: "relay",
    },
  },
  // noTurn: {
  //   host: 'signal.playcast.io',
  //   port: 443,
  //   secure: true,
  //   debug: 0,
  //   config: {
  //     iceServers: generateIceServers(false), // Call without TURN servers

  //     /*
  //     //Use twitchcon STUN/TURN servers
  //     iceServers: [
  //       {
  //         urls: 'stun:stun.playcast.io:5349',
  //         username: 'stunadmin',
  //         credential: 'm7te8VLHjDi4sA6KTG8g',
  //       },
  //     ],
  //     */
  //   },
  // },
};

// console.log(JSON.stringify(serviceConfigs, null, 2));

export const h264Config: H264EncodingConfig = {
  enableSpsPpsIdrInKeyframe: true,
  enableMaxBitrate: true,
  enableMinBitrate: true,
  enableStartBitrate: true,
  maxBitrate: 10 * MBit,
  minBitrate: 1 * MBit,
  startBitrate: 2 * MBit,
};

const defaultPreset: PlaycastWebrtcPresetGroup = {
  default: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
      disableFeedbackTypes: {
        googRemb: false,
        transportCc: false,
        ccmFir: false,
        nack: false,
        nackPli: false,
      },
    },
    videoReceive: {
      encoder: ['h264'],
      jitterBufferTarget: 0, // 0ms latency target for low latency
    },
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  },
  hostShadowCam00Video: {
    videoSend: {
      framerate: 60,
      bitrate: 2.9 * MBit,
      keyframeInterval: 30000,
      degradationPreference: 'maintain-framerate',
    },
    videoReceive: {
      encoder: ['h264'],
      jitterBufferTarget: 250, // Lower latency for shadow cam
    },
  },
  hostShadowCam01Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
  },
  hostShadowCam02Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
  },
  hostShadowCam03Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
    videoReceive: {
      encoder: ['h264'],
    },
  },
  hostShadowCam04Video: {
    videoSend: {
      framerate: 60,
      bitrate: 6.6 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
    videoReceive: {
      encoder: ['h264'],
    },
  },
  hostShadowCam05Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
  },
  hostShadowCam06Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
  },
  hostShadowCam07Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
  },
  hostShadowCam08Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
  },
  hostShadowCam09Video: {
    videoSend: {
      framerate: 60,
      bitrate: 4 * MBit,
      keyframeInterval: 1000,
      degradationPreference: 'maintain-framerate',
    },
  },
  remoteHostVideo: {
    videoSend: {
      framerate: 1,
      bitrate: 4 * MBit,
    },
    videoReceive: {
      encoder: ['h264'],
    },
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  },
};

// For each channel, we use that channel but backfill with default.
const fullPreset: PlaycastWebrtcPresetGroup = Object.fromEntries(
  Object.entries(defaultPreset).map(([channel, channelPreset]) => {
    // Skip merging if this is the default channel
    if (channel === 'default') {
      return [channel, channelPreset];
    }

    // Deep merge the channel preset with default
    const mergedPreset: PlaycastWebrtcPreset = {
      // Start with default settings
      ...defaultPreset.default,
      // Override with channel-specific settings
      ...channelPreset,
    };

    // Handle videoSend separately to ensure required properties
    if (defaultPreset.default?.videoSend || channelPreset.videoSend) {
      mergedPreset.videoSend = {
        // Ensure required properties from default
        framerate: channelPreset.videoSend?.framerate ?? defaultPreset.default?.videoSend?.framerate ?? 60,
        bitrate: channelPreset.videoSend?.bitrate ?? defaultPreset.default?.videoSend?.bitrate ?? 4 * MBit,
        // Optional properties
        ...((defaultPreset.default?.videoSend && {
          keyframeInterval: defaultPreset.default.videoSend.keyframeInterval,
          degradationPreference: defaultPreset.default.videoSend.degradationPreference,
        }) ||
          {}),
        ...(channelPreset.videoSend || {}),
      };
    }

    // Handle videoReceive
    if (defaultPreset.default?.videoReceive || channelPreset.videoReceive) {
      mergedPreset.videoReceive = {
        ...(defaultPreset.default?.videoReceive || {}),
        ...(channelPreset.videoReceive || {}),
      };
    }

    // Handle audio
    if (defaultPreset.default?.audio || channelPreset.audio) {
      mergedPreset.audio = {
        echoCancellation: channelPreset.audio?.echoCancellation ?? defaultPreset.default?.audio?.echoCancellation ?? true,
        noiseSuppression: channelPreset.audio?.noiseSuppression ?? defaultPreset.default?.audio?.noiseSuppression ?? true,
        autoGainControl: channelPreset.audio?.autoGainControl ?? defaultPreset.default?.audio?.autoGainControl ?? true,
      };
    }

    return [channel, mergedPreset];
  }),
) as PlaycastWebrtcPresetGroup;
export const defaultQualityPresetStore = makeStateStore(fullPreset);

export const defaultStatsInterval = 1000;
